import { FormGroup } from '@angular/forms';
import { isAfter, isBefore, isFuture } from 'date-fns';
import { isEqual } from 'lodash-es';
import { addRemoveError, getControl } from '@shared/helpers/form.helper';
import { isNilOrEmpty } from '@shared/helpers/utils.helper';
import { INCONSISTENT_CHOICE, REQUIRED_STATUS } from '@shared/constants/shared.constants';
import { FormGroupRules } from '@shared/models/interfaces/form-group-rules.model';
import { toNum } from '@shared/helpers/math.helper';
import { getDatiIdentificativiControl, getDatiIdentificativiGroup } from './helpers/dati-identificativi-form-validators.helper';

export const datiIdentificativiDelegheDateRules: FormGroupRules = {
    my_CkDelegCompOrgano: (form: FormGroup) => {
        const datiIdentificativiGroup = getDatiIdentificativiGroup(form);
        const my_CkDelegCompOrganoControl = getControl(datiIdentificativiGroup, 'my_CkDelegCompOrgano');
        const my_QualitaRappres: string = getControl(datiIdentificativiGroup, 'my_QualitaRappres')?.value;
        const errors = my_CkDelegCompOrganoControl.errors ? my_CkDelegCompOrganoControl.errors : {};
        const showError =
            !isNilOrEmpty(my_QualitaRappres) && my_QualitaRappres?.toLowerCase() === 'delegato dal legale rappresentante' && isNilOrEmpty(my_CkDelegCompOrganoControl?.value);

        addRemoveError(showError, errors, 'my_CkDelegCompOrgano', {
            status: REQUIRED_STATUS,
        });

        my_CkDelegCompOrganoControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_CkDelegProcura: (form: FormGroup) => {
        const datiIdentificativiGroup = getDatiIdentificativiGroup(form);
        const my_CkDelegProcuraControl = getControl(datiIdentificativiGroup, 'my_CkDelegProcura');
        const my_QualitaRappres: string = getControl(datiIdentificativiGroup, 'my_QualitaRappres')?.value;
        const my_CkDelegCompOrgano: string = getControl(datiIdentificativiGroup, 'my_CkDelegCompOrgano')?.value;
        const showRequiredError =
            !isNilOrEmpty(my_QualitaRappres) &&
            my_QualitaRappres?.toLowerCase() === 'delegato dal legale rappresentante' &&
            my_CkDelegCompOrgano === '0' &&
            isNilOrEmpty(my_CkDelegProcuraControl?.value);
        const showLegaleRappresentanteError =
            !isNilOrEmpty(my_QualitaRappres) &&
            my_QualitaRappres?.toLowerCase() === 'delegato dal legale rappresentante' &&
            my_CkDelegCompOrgano === '0' &&
            my_CkDelegProcuraControl.value === '0';
        const errors = my_CkDelegProcuraControl.errors ? my_CkDelegProcuraControl.errors : {};

        addRemoveError(showRequiredError, errors, 'my_CkDelegProcura', {
            status: REQUIRED_STATUS,
        });

        addRemoveError(showLegaleRappresentanteError, errors, 'my_CkDelegProcura', {
            status: 'Attenzione: richiedere la presenza del legale rappresentante',
            message: 'Richiedere la presenza del legale rappresentante',
        });

        my_CkDelegProcuraControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_DataUltCert: (form: FormGroup) => {
        const datiIdentificativiGroup = getDatiIdentificativiGroup(form);
        const my_DataUltCertControl = getControl(datiIdentificativiGroup, 'my_DataUltCert');
        const my_CkMaiCertificataBilControl = getControl(datiIdentificativiGroup, 'my_CkMaiCertificataBil');
        const showRequiredError = (my_CkMaiCertificataBilControl.value === '0' || !my_CkMaiCertificataBilControl?.value) && isNilOrEmpty(my_DataUltCertControl?.value);
        const showInconsistentChoice = !isNilOrEmpty(my_DataUltCertControl?.value) && toNum(my_CkMaiCertificataBilControl.value) === 1;
        const errors = my_DataUltCertControl.errors ? my_DataUltCertControl.errors : {};

        addRemoveError(showRequiredError, errors, 'my_DataUltCertinvalid', {
            status: REQUIRED_STATUS,
        });

        addRemoveError(showInconsistentChoice, errors, 'my_DataUltCertinvalid', {
            status: INCONSISTENT_CHOICE,
            message: "Eliminare la data di ultima certificazione bilancio o deselezionare la casella 'Mai certificata'",
        });

        my_DataUltCertControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_CkMaiCertificataBil: (form: FormGroup) => {
        const datiIdentificativiGroup = getDatiIdentificativiGroup(form);
        const my_CkMaiCertificataBilControl = getControl(datiIdentificativiGroup, 'my_CkMaiCertificataBil');
        const my_DataUltCertControl = getControl(datiIdentificativiGroup, 'my_DataUltCert');
        const showInsertSomethingsError = (!my_CkMaiCertificataBilControl?.value || my_CkMaiCertificataBilControl?.value === '0') && isNilOrEmpty(my_DataUltCertControl?.value);
        const showDeleteOneResponseError = my_CkMaiCertificataBilControl?.value == '1' && !isNilOrEmpty(my_DataUltCertControl?.value);
        const errors = my_CkMaiCertificataBilControl.errors ? my_CkMaiCertificataBilControl.errors : {};

        addRemoveError(showInsertSomethingsError, errors, 'my_CkMaiCertificataBil', {
            status: "Attenzione: inserire una data o selezionare l'opzione 'Mai certificata'",
        });

        addRemoveError(showDeleteOneResponseError, errors, 'my_CkMaiCertificataBil', {
            status: 'Attenzione: eliminare la data di ultima certificazione bilancio o deselezionare la casella "Mai certificata"',
        });

        my_CkMaiCertificataBilControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_DataCostituz: (form: FormGroup) => {
        const my_DataCostituzControl = getDatiIdentificativiControl(form, 'my_DataCostituz');
        const errors = my_DataCostituzControl.errors ? my_DataCostituzControl.errors : {};
        const data = new Date(my_DataCostituzControl.value);
        const resultEqual = isEqual(data, new Date('1900-01-01'));
        const resultLater = isAfter(data, new Date('2079-06-06'));
        const resultNow = isFuture(data);

        addRemoveError(isNilOrEmpty(my_DataCostituzControl?.value), errors, 'my_DataCostituz', {
            status: REQUIRED_STATUS,
        });

        addRemoveError(resultEqual, errors, 'my_DataCostituz', {
            status: 'Attenzione: verificare la data di costituzione',
            message: 'Verificare la data di costituzione',
        });

        addRemoveError(resultLater, errors, 'my_DataCostituz', {
            status: 'Attenzione: data non consentita',
            message: 'Verificare la data di costituzione',
        });

        addRemoveError(resultNow, errors, 'my_DataCostituz', {
            status: 'Attenzione: verificare la data di costituzione',
            message: 'La data di costituzione non può essere futura',
        });

        my_DataCostituzControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_DataTermine: (form: FormGroup) => {
        const datiIdentificativiGroup = getDatiIdentificativiGroup(form);
        const my_DataTermineControl = getControl(datiIdentificativiGroup, 'my_DataTermine');
        const my_CkScadenzaSocietaControl = getControl(datiIdentificativiGroup, 'my_CkScadenzaSocieta');
        const errors = my_DataTermineControl.errors ? my_DataTermineControl.errors : {};

        addRemoveError(isNilOrEmpty(my_DataTermineControl?.value) && my_CkScadenzaSocietaControl?.value === '0', errors, 'my_DataTermine', {
            status: 'Attenzione: specificare la data termine della società',
            message: 'Specificare la data termine della società',
        });

        my_DataTermineControl.setErrors(isEqual(errors, {}) ? null : errors);
    },
};
